import { FieldValues, Path, UseFormReturn, useWatch } from "react-hook-form";
import { LabelAndSelect } from "../reuseables/form.comp";
import useRetailList from "../../hooks/useRetailList.hook";
import { useTranslation } from "../../core/translation.core";
import { useEffect } from "react";

type PlacementRestrictionFormProps<T extends FieldValues> = {
  methods: UseFormReturn<T>;
  typeFieldName: Path<T>;
  eshopFieldName: Path<T>;
  retailIdsFieldName: Path<T>;
};

export default function PlacementRestrictionForm<T extends FieldValues>({
  methods,
  typeFieldName,
  eshopFieldName,
  retailIdsFieldName,
}: PlacementRestrictionFormProps<T>) {
  const { t, msg } = useTranslation();
  const restrictionType = useWatch({
    name: typeFieldName,
    control: methods.control,
  });

  const { data: retailShops } = useRetailList();
  const restrictToEshop = useWatch({
    name: eshopFieldName,
    control: methods.control,
  }) as boolean | undefined;

  const restrictToRetailIds = useWatch({
    name: retailIdsFieldName,
    control: methods.control,
  }) as string[] | undefined;

  const toAllRetailsFieldName = eshopFieldName.replace(/\.restrictToEshop$/, ".toAllRetails") as Path<T>;
  const toAllRetails = useWatch({
    name: toAllRetailsFieldName,
    control: methods.control,
  }) as boolean | undefined;

  useEffect(() => {
    if (restrictionType && restrictionType !== "none") {
      if (restrictToEshop === undefined) {
        methods.setValue(eshopFieldName, false as any);
      }
      if (!restrictToRetailIds) {
        methods.setValue(retailIdsFieldName, [] as any);
      }
      if (toAllRetails === undefined) {
        methods.setValue(toAllRetailsFieldName, false as any);
      }
    }
    if (restrictionType !== "inclusion" && toAllRetails) {
      methods.setValue(toAllRetailsFieldName, false as any);
    }
  }, [
    restrictionType,
    methods,
    eshopFieldName,
    retailIdsFieldName,
    toAllRetailsFieldName,
    restrictToEshop,
    restrictToRetailIds,
    toAllRetails,
  ]);

  const handleEshopToggle = () => {
    methods.setValue(eshopFieldName, !restrictToEshop as any);
  };

  const handleToAllRetailsToggle = () => {
    methods.setValue(toAllRetailsFieldName, !toAllRetails as any);
  };

  const handleRetailToggle = (retailId: string) => {
    const current = (restrictToRetailIds || []) as string[];
    const set = new Set(current);
    if (set.has(retailId)) {
      set.delete(retailId);
    } else {
      set.add(retailId);
    }
    methods.setValue(retailIdsFieldName as any, Array.from(set) as any);
  };

  return (
    <div>
      <LabelAndSelect
        methods={methods}
        label={t(msg.ship.placementRestriction)}
        topLabelCss="block w-full"
        fieldName={typeFieldName}
        options={[
          { label: t(msg.ship.none), value: "none" },
          { label: t(msg.ship.inclusion), value: "inclusion" },
          { label: t(msg.ship.exclusion), value: "exclusion" },
        ]}
      />

      {restrictionType && restrictionType !== "none" ? (
        <div className="space-y-4 mt-4">
          <div>
            <label className="flex items-center hover:font-semibold hover:text-blue-600 dark:hover:text-blue-400">
              <input
                type="checkbox"
                name={eshopFieldName}
                checked={restrictToEshop || false}
                onChange={handleEshopToggle}
                className="mr-2"
              />
              {t(msg.placement.eshop)}
            </label>
            {restrictionType === "inclusion" && (
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 ml-6">
                {restrictToEshop ? "eShop will be included" : "eShop will be excluded"}
              </p>
            )}
            {restrictionType === "exclusion" && (
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 ml-6">
                {restrictToEshop ? "eShop will be excluded" : "eShop will be included"}
              </p>
            )}
          </div>

          {restrictionType === "inclusion" && (
            <div>
              <label className="flex items-center hover:font-semibold hover:text-blue-600 dark:hover:text-blue-400">
                <input
                  type="checkbox"
                  name={toAllRetailsFieldName}
                  checked={toAllRetails || false}
                  onChange={handleToAllRetailsToggle}
                  className="mr-2"
                />
                {t(msg.placement.toAllRetails)}
              </label>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 ml-6">
                {toAllRetails ? "All retail shops will be included" : "Select specific retail shops below"}
              </p>
            </div>
          )}

          <div id="retail-shops-list">
            <span className="block text-xs font-semibold mb-2">{t(msg.placement.retail)}</span>
            {restrictionType === "inclusion" && toAllRetails && (
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">All retail shops are included</p>
            )}
            {restrictionType === "inclusion" && !toAllRetails && (restrictToRetailIds?.length || 0) === 0 && (
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                No retail shops selected: All retail shops will be excluded
              </p>
            )}
            {restrictionType === "exclusion" && (restrictToRetailIds?.length || 0) === 0 && (
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                No retail shops selected: All retail shops will be included
              </p>
            )}
            <div className="space-y-1">
              {retailShops?.map((retailShop) => (
                <label
                  data-label={retailShop.name}
                  key={retailShop.id}
                  className={`flex items-center hover:font-semibold hover:text-blue-600 dark:hover:text-blue-400 ${
                    restrictionType === "inclusion" && toAllRetails ? "opacity-50 pointer-events-none" : ""
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={restrictToRetailIds?.includes(retailShop.id) || false}
                    onChange={() => handleRetailToggle(retailShop.id)}
                    disabled={restrictionType === "inclusion" && !!toAllRetails}
                    className="mr-2"
                  />
                  {retailShop.name}
                </label>
              ))}
            </div>
            {restrictionType === "inclusion" && !toAllRetails && (restrictToRetailIds?.length || 0) > 0 && (
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">Only selected retail shops will be included</p>
            )}
            {restrictionType === "exclusion" && (restrictToRetailIds?.length || 0) > 0 && (
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">Selected retail shops will be excluded</p>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}
